import { ExitToApp } from "@mui/icons-material";
import { Box } from "@mui/material";
import { ReactElement } from "react";

import { ListTenantsQuery } from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import { ExternalLink } from "~frontend/components/Link";
import Table from "~frontend/components/Table";
import Archive from "~frontend/components/icons/Archive";
import { useTenantSlug } from "~frontend/providers/TenantSlugProvider";

export type TenantTableRow = ListTenantsQuery["listTenants"][0];
function BandwidthConfig({ tenant }: { tenant: TenantTableRow }) {
  return (
    <Box>
      {(
        [
          {
            name: "Registered",
            key: "bandwidthRegisteredApplicationInfo",
          },
          {
            name: "Unregistered",
            key: "bandwidthUnregisteredApplicationInfo",
          },
          {
            name: "Short Code",
            key: "bandwidthShortCodeApplicationInfo",
          },
          {
            name: "Toll Free",
            key: "bandwidthTollFreeApplicationInfo",
          },
        ] as const
      ).map(({ name, key }) => (
        <FlexBox key={`bw-config-${key}`} sx={{ mb: "2px" }}>
          <Box display="inline" sx={{ width: "90px" }}>
            {name}:{" "}
          </Box>
          {tenant[key] == null ? (
            <>Not Configured</>
          ) : (
            <>
              <ExternalLink
                href={`https://dashboard.bandwidth.com/portal/r/a/${tenant[key].accountId}/account/locations/${tenant[key].subaccountId}/${tenant[key].locationId}`}
              >
                {tenant[key].isDefault
                  ? "Default Location"
                  : `Dedicated Location: ${tenant[key].subaccountId} / ${tenant[key].locationId}`}
              </ExternalLink>{" "}
            </>
          )}
        </FlexBox>
      ))}
    </Box>
  );
}

export default function TenantTable({
  rows,
  page,
  handleChangePage,
  rowsPerPage,
  handleChangeRowsPerPage,
  totalCount,
  onDeactivateTenant,
}: {
  rows: TenantTableRow[];
  page: number;
  handleChangePage: (newPage: number) => void;
  rowsPerPage: number;
  handleChangeRowsPerPage: (newRowsPerPage: number) => void;
  totalCount: number;
  onDeactivateTenant: (tenantId: string) => Promise<void>;
}): ReactElement {
  const currentTenantSlug = useTenantSlug();
  return (
    <Table
      columns={[
        {
          key: "slug",
          title: "Slug",
          renderValue: tenant => (
            <span data-testid="tenant-slug-cell">{tenant.slug}</span>
          ),
          align: "left",
        },
        {
          key: "name",
          title: "Name",
          renderValue: tenant => (
            <span data-testid="tenant-name-cell">{tenant.name}</span>
          ),
          align: "left",
        },
        {
          key: "bandwidthConfigured",
          title: "Bandwidth Configuration",
          renderValue: tenant => <BandwidthConfig tenant={tenant} />,

          align: "left",
        },
        {
          key: "createdAt",
          title: "Created At",
          renderValue: tenant => new Date(tenant.createdAt).toLocaleString(),

          align: "left",
        },
        {
          key: "deactivatedAt",
          title: "Deactivated At",
          renderValue: tenant =>
            tenant.deactivatedAt
              ? new Date(tenant.deactivatedAt).toLocaleString()
              : "",
          align: "left",
        },
      ]}
      rows={rows}
      rowActions={tenant => [
        ...(tenant.deactivatedAt
          ? []
          : [
              {
                id: "deactivate",
                type: "button" as const,
                icon: <Archive />,
                tooltipLabel: "Deactivate Tenant",
                onClick: () => onDeactivateTenant(tenant.id),
                confirm: {
                  title: `Deactivate ${tenant.name}`,
                  description: `Deactivate ${tenant.name}?`,
                },
              },
            ]),
        {
          id: "go-to-tenant",
          type: "link",
          icon: <ExitToApp />,
          tooltipLabel: "Go To Tenant",
          href: tenant => {
            const host = window.location.host.replace(
              new RegExp(`^${currentTenantSlug}.`),
              `${tenant.slug}.`
            );

            return `${window.location.protocol}//${host}`;
          },
          target: "_blank",
        },
      ]}
      emptyState="No tenants found"
      totalCount={totalCount}
      rowsPerPage={rowsPerPage}
      onChangeRowsPerPage={handleChangeRowsPerPage}
      page={page}
      onChangePage={handleChangePage}
    />
  );
}
